import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { eur } from "@/lib/api";
import { PageHeader, Btn, Modal, inputCls } from "@/components/common";
import InvoiceForm from "@/components/InvoiceForm";
import { KIND_LABEL, fmtDateIt } from "@/lib/invoice";
import { Plus, Pencil, Trash2, FileText, Receipt } from "lucide-react";

const KIND_CLS = { acconto: "bg-amber-100 text-amber-700", saldo: "bg-emerald-100 text-emerald-700", altro: "bg-slate-100 text-slate-600" };

export default function Fatture() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [year, setYear] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/invoices").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const years = [...new Set(items.map((i) => (i.date || "").slice(0, 4)).filter(Boolean))].sort().reverse();
  const s = q.trim().toLowerCase();
  const filtered = items.filter((i) => (!year || (i.date || "").startsWith(year)) &&
    (!s || `${i.number} ${i.client?.name || ""} ${i.guest_name || ""}`.toLowerCase().includes(s)));
  const total = filtered.reduce((a, i) => a + Number(i.total || 0), 0);

  const remove = async (inv) => {
    if (!window.confirm(`Eliminare la fattura ${inv.number}?`)) return;
    await api.delete(`/invoices/${inv.id}`);
    toast.success("Fattura eliminata");
    load();
  };

  return (
    <div>
      <PageHeader title="Fatture" subtitle="Fatture intestate alle agenzie viaggi · Blu Lodge Maldives">
        <Btn data-testid="new-invoice-btn" onClick={() => setEditing({})}><Plus size={16} /> Nuova fattura</Btn>
      </PageHeader>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input data-testid="invoices-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca numero, agenzia o ospite…" className={`${inputCls} sm:w-72`} />
        <select data-testid="invoices-year" value={year} onChange={(e) => setYear(e.target.value)} className="px-3 py-2 rounded-md border border-slate-300 text-sm bg-white outline-none focus:ring-2 focus:ring-sky-500">
          <option value="">Tutti gli anni</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="ml-auto text-sm text-slate-500" data-testid="invoices-summary">{filtered.length} fatture · <b className="text-slate-800 tabular">{eur(total)}</b></span>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
              <th className="px-3 py-2.5">Numero</th><th className="px-3 py-2.5">Data</th><th className="px-3 py-2.5">Tipo</th>
              <th className="px-3 py-2.5">Cliente</th><th className="px-3 py-2.5">Ospite</th>
              <th className="px-3 py-2.5 text-right">Totale</th><th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody data-testid="invoices-table">
            {filtered.map((i) => (
              <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`invoice-row-${i.id}`}>
                <td className="px-3 py-2.5 font-semibold text-slate-800 tabular">{i.number}</td>
                <td className="px-3 py-2.5 text-slate-600">{fmtDateIt(i.date)}</td>
                <td className="px-3 py-2.5"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${KIND_CLS[i.kind] || KIND_CLS.altro}`}>{KIND_LABEL[i.kind] || i.kind}</span></td>
                <td className="px-3 py-2.5 text-slate-700">{i.client?.name}</td>
                <td className="px-3 py-2.5 text-slate-600">{i.guest_name}</td>
                <td className="px-3 py-2.5 text-right tabular font-medium">{eur(i.total)}</td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`invoice-open-${i.id}`} title="Apri / PDF / WhatsApp" onClick={() => window.open(`/fattura/${i.id}`, "_blank")} className="p-1.5 text-slate-400 hover:text-sky-600"><FileText size={15} /></button>
                    <button data-testid={`invoice-edit-${i.id}`} title="Modifica" onClick={() => setEditing(i)} className="p-1.5 text-slate-400 hover:text-sky-600"><Pencil size={15} /></button>
                    <button data-testid={`invoice-del-${i.id}`} title="Elimina" onClick={() => remove(i)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-12 text-center text-slate-400"><Receipt className="mx-auto mb-2" /> Nessuna fattura</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? `Modifica fattura ${editing.number}` : "Nuova fattura"} wide>
        {editing && <InvoiceForm initial={editing.id ? editing : null} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      </Modal>
    </div>
  );
}
